"use client";

import { apiFetch } from "@/lib/api";
import { Textarea, Input, Button, Form, Modal, ModalBody, ModalContent, ModalHeader, addToast, DatePicker, DateValue } from "@heroui/react";
import { IconFile, IconPlus } from "@tabler/icons-react";
import { useSession } from "next-auth/react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useRef, useState } from "react";
import Image from "next/image";
import { I18nProvider } from "@react-aria/i18n";
import { getLocalTimeZone, now } from "@internationalized/date";

export default function NewProjectModal() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { data: session, update } = useSession();
  const modal = searchParams.get("modal");
  const [isLoading, setIsLoading] = useState(false);
  const [preview, setPreview] = useState("");
  const [fileName, setFileName] = useState("");
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  const [tagValue, setTagValue] = useState("");
  const [datas, setDatas] = useState({
    inicio: now(getLocalTimeZone()),
    fim: now(getLocalTimeZone()),
  });

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setIsLoading(true);

    setFileName(file.name);
    setPreview(URL.createObjectURL(file));

    setIsLoading(false);
  };

  const handleImageUpload = async (file: File | null): Promise<string | undefined> => {
    if (!file || file.size === 0) return undefined;

    const fd = new FormData();
    fd.append("imagem", file, file.name);

    const resImage = await apiFetch("/users/me/avatar", {
      method: "POST",
      headers: { Authorization: `Bearer ${session?.accessToken}` },
      body: fd,
    });

    if (!resImage.ok) {
      addToast({
        title: "Erro ao enviar imagem",
        color: "danger",
      });
      return undefined;
    }

    const imageData = await resImage.json();
    return imageData.url as string;
  };

  const formatToBackend = (zonedDateTime) => {
    if (!zonedDateTime) return null;

    // transforma em Date JS na timezone local
    const jsDate = zonedDateTime.toDate(getLocalTimeZone());

    const pad = (n) => String(n).padStart(2, "0");

    const year = jsDate.getFullYear();
    const month = pad(jsDate.getMonth() + 1);
    const day = pad(jsDate.getDate());
    const hours = pad(jsDate.getHours());
    const minutes = pad(jsDate.getMinutes());
    const seconds = pad(jsDate.getSeconds());

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setIsLoading(true);
    const formData = new FormData(e.currentTarget);

    const file = formData.get("imagem") as File | null;
    const imageUrl = await handleImageUpload(file);

    const payload: any = {
      titulo: formData.get("titulo") as string,
      descricao: formData.get("descricao") as string,
      texto: formData.get("texto") as string,
      tags: tags,
      data_inicio: formatToBackend(datas.inicio), // ex: "2025-03-01T08:00:00"
      data_fim: formatToBackend(datas.fim),
    };

    if (imageUrl) {
      payload.imagem_atividade = imageUrl;
    }

    console.log(JSON.stringify(payload));

    const res = await apiFetch("/projetos/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session?.accessToken}`,
      },
      body: JSON.stringify(payload),
    });

    if (res.ok) {
      addToast({
        title: "Projeto enviado com sucesso",
        color: "success",
      });
      setIsLoading(false);
      router.push("/");
      update();
    }

    if (!res.ok) {
      addToast({
        title: "Erro ao enviar o projeto",
        description: "Tente novamente mais tarde",
        color: "danger",
      });
      setIsLoading(false);
    }
  };

  const onOpenChange = () => {
    router.push(pathname);
  };

  const handleAddTag = () => {
    const value = tagValue.trim();

    if (!value) return;
    if (tags.includes(value)) return; // evita tags duplicadas

    setTags((prev) => [...prev, value]);
    setTagValue("");
  };

  return (
    <Modal isOpen={modal === "newProject"} onOpenChange={onOpenChange} backdrop="blur" placement="center" className="mx-4 md:min-w-1/2">
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader>Criar novo projeto</ModalHeader>
            <ModalBody>
              <Form className="gap-4 flex flex-col" onSubmit={handleSubmit}>
                <Input name="titulo" label="Titulo" placeholder="Digite o titulo do projeto" className="min-w-full" isRequired />
                <div className="w-full flex flex-row gap-4">
                  <I18nProvider locale="pt-BR">
                    <DatePicker
                      label="Data de início"
                      hideTimeZone
                      showMonthAndYearPickers
                      value={datas.inicio}
                      onChange={(value) => setDatas((prev) => ({ ...prev, inicio: value }))}
                      variant="bordered"
                    />
                    <DatePicker
                      label="Data de término"
                      hideTimeZone
                      showMonthAndYearPickers
                      value={datas.fim}
                      onChange={(value) => setDatas((prev) => ({ ...prev, fim: value }))}
                      variant="bordered"
                    />
                  </I18nProvider>
                </div>
                <Input name="descricao" label="Descrição" placeholder="Digite a descrição do projeto (breve)" className="min-w-full" isRequired />
                <Input
                  label="Tags"
                  placeholder="Digite uma tag e clique no +"
                  value={tagValue}
                  maxLength={10}
                  onChange={(e) => setTagValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                  description={tags.length > 0 ? `Tags: ${tags.join(", ")}` : "Nenhuma tag adicionada ainda"}
                  endContent={
                    <Button isIconOnly variant="light" size="sm" onPress={handleAddTag}>
                      <IconPlus size={20} />
                    </Button>
                  }
                />
                <Textarea
                  name="texto"
                  label="Texto do projeto"
                  placeholder="Descreva como é o projeto"
                  maxLength={2000}
                  className="min-w-full"
                  isRequired
                />
                <div className="w-full flex flex-col gap-4">
                  {preview && (
                    <Image
                      quality={100}
                      height={1000}
                      width={800}
                      src={preview}
                      alt="Preview da imagem"
                      className="h-50 w-full rounded-2xl object-cover"
                    />
                  )}
                  <div className="flex min-w-full">
                    <Button
                      className="w-full text-black cursor-pointer"
                      color="primary"
                      variant="flat"
                      onPress={() => {
                        fileRef.current?.click();
                      }}
                      startContent={<IconFile size={25} />}
                    >
                      Enviar Imagem
                    </Button>
                    <input ref={fileRef} name="imagem" type="file" accept="image/*" className="hidden" onChange={handleSelect} />
                  </div>
                </div>
                <div className="min-w-full flex gap-2 justify-end">
                  <Button color="danger" type="button" onPress={onClose} isLoading={isLoading}>
                    Fechar
                  </Button>
                  <Button type="submit" color="success" isLoading={isLoading}>
                    Enviar
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
